import { twMerge } from 'tailwind-merge';

import { AdvertisementFieldAccessor } from '~/api/field-accessors';
import type { CampaignTransferResponseV2 } from '~/api/v2/mall/campaign/transfer/campaign-id';
import type { OfferDetailResponseV2 } from '~/api/v2/mall/offer/campaign-id';
import type { ShopDetailResponseV2 } from '~/api/v2/mall/shop/campaign-id';
import type { CampaignInfoV2 } from '~/api/v2/schemas/campaign-info';

import { formatPoint, formatRate } from './format';
import { getPointData } from './get-point-data';

type CampaignData = CampaignInfoV2 | ShopDetailResponseV2 | OfferDetailResponseV2 | CampaignTransferResponseV2;

export function SitePoint({
  data,
  size = 'medium',
}: {
  data: CampaignData;
  size?: 'small' | 'smToMd' | 'medium' | 'large';
}) {
  const { isRate, value, firstAd, regularValue, hasPointup, hasMultiple } = getPointData(data);

  const textSizeMap = {
    small: 'text-caption-s',
    smToMd: 'text-caption-s md:text-body-s',
    medium: 'text-body-s',
    large: 'text-body-s md:text-body-m',
  };
  const numSizeMap = {
    small: 'text-headline-s',
    smToMd: 'text-headline-s md:text-headline-m',
    medium: 'text-headline-m',
    large: 'text-headline-m md:text-headline-l',
  };
  const gapMap = {
    small: 'gap-1',
    smToMd: 'gap-1',
    medium: 'gap-1',
    large: 'gap-1.5',
  };

  const textSize = textSizeMap[size];
  const numSize = numSizeMap[size];
  const gap = gapMap[size];

  if (hasPointup && isRate) {
    return (
      <div
        className={twMerge('flex flex-wrap items-baseline justify-center whitespace-nowrap', gap)}
        data-testid="site-point">
        <span className={textSize}>株引換券</span>
        <span className={twMerge(textSize, 'inline-flex line-through')}>
          {regularValue !== undefined ? formatRate(regularValue) : '-'}%
        </span>
        <span className={textSize}>→</span>
        <span className="text-brand">
          <span className={twMerge(numSize, size === 'medium' && 'ml-[2px]')}>
            {firstAd ? formatRate(AdvertisementFieldAccessor.net(firstAd)) : '-'}
          </span>
          <span className={textSize}>%</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={twMerge('flex flex-wrap items-baseline whitespace-nowrap', size === 'small' && 'justify-center', gap)}
      data-testid="site-point">
      <span className={textSize}>株引換券</span>
      {hasMultiple && <span className={twMerge(textSize, 'mr-[2px] md:mr-[0px] lg:mr-[2px]')}>最大</span>}
      <span className={hasPointup ? 'text-brand' : ''}>
        <span className={numSize}>{formatPoint(value, isRate)}</span>
        <span className={textSize}>{isRate ? '%' : '枚'}</span>
      </span>
    </div>
  );
}
